import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Gazzetta from "./pages/Gazzetta";
import Concorsi from "./pages/Concorsi";
import Curriculum from "./pages/Curriculum";
import LDL from "./pages/LDL";
import Scontrini from "./pages/Scontrini";
import Aziende from "./pages/Aziende";
import Utenti from "./pages/Utenti";
import RuoliAzienda from "./pages/RuoliAzienda";
import Documenti from "./pages/Documenti";
import Profilo from "./pages/Profilo";
import Scuola from "./pages/Scuola";
import Ospedale from "./pages/Ospedale";
import Banca from "./pages/Banca";
import Multe from "./pages/Multe";
import Seggi from "./pages/Seggi";
import Negozio from "./pages/Negozio";
import Assunzioni from "./pages/Assunzioni";
import DashboardAdmin from "./pages/DashboardAdmin";
import Forum from "./pages/Forum";
import GestioneAziende from "./pages/GestioneAziende";
import GestionaleAzienda from "./pages/GestionaleAzienda";
import Patenti from "./pages/Patenti";
import GestioneBan from "./pages/GestioneBan";
import AdminLog from "./pages/AdminLog";
import Partiti from "./pages/Partiti";
import PartiteIVA from "./pages/PartiteIVA";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  
  // Se l'utente è bannato, può accedere solo al forum
  const userIsBanned = user?.ban_info?.is_banned === true;
  if (userIsBanned && location.pathname !== '/forum') {
    return <Navigate to="/forum" replace />;
  }
  
  return children;
};

// Route speciale per il Forum - anche gli utenti bannati possono accedere
const ForumRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/" replace />;
};

const ScontrinoRoute = ({ children }) => {
  const { user, loading, canViewScontrini, hasAzienda } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canViewScontrini() || !hasAzienda()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const SindacatoAdminRoute = ({ children }) => {
  const { user, loading, isSindacatoAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!isSindacatoAdmin()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const DocumentiRoute = ({ children }) => {
  const { user, loading, canViewDocumenti } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canViewDocumenti()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const ManageDipendentiRoute = ({ children }) => {
  const { user, loading, canManageDipendenti } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canManageDipendenti()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

// Route per moduli specifici aziendali
const ScuolaRoute = ({ children }) => {
  const { user, loading, canAccessScuola } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canAccessScuola()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const OspedaleRoute = ({ children }) => {
  const { user, loading, canAccessOspedale } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canAccessOspedale()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const BancaRoute = ({ children }) => {
  const { user, loading, canAccessBanca } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canAccessBanca()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const MulteRoute = ({ children }) => {
  const { user, loading, canAccessMulte } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canAccessMulte()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const SeggiRoute = ({ children }) => {
  const { user, loading, canAccessSeggi } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!canAccessSeggi()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isSindacatoAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!isSindacatoAdmin()) return <Navigate to="/dashboard" replace />;
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <Layout><Dashboard /></Layout>
        </PrivateRoute>
      } />
      <Route path="/gazzetta" element={
        <PrivateRoute>
          <Layout><Gazzetta /></Layout>
        </PrivateRoute>
      } />
      <Route path="/concorsi" element={
        <PrivateRoute>
          <Layout><Concorsi /></Layout>
        </PrivateRoute>
      } />
      <Route path="/curriculum" element={
        <PrivateRoute>
          <Layout><Curriculum /></Layout>
        </PrivateRoute>
      } />
      <Route path="/ldl" element={
        <PrivateRoute>
          <Layout><LDL /></Layout>
        </PrivateRoute>
      } />
      <Route path="/scontrini" element={
        <ScontrinoRoute>
          <Layout><Scontrini /></Layout>
        </ScontrinoRoute>
      } />
      <Route path="/aziende" element={
        <SindacatoAdminRoute>
          <Layout><Aziende /></Layout>
        </SindacatoAdminRoute>
      } />
      <Route path="/ruoli-azienda" element={
        <SindacatoAdminRoute>
          <Layout><RuoliAzienda /></Layout>
        </SindacatoAdminRoute>
      } />
      <Route path="/utenti" element={
        <ManageDipendentiRoute>
          <Layout><Utenti /></Layout>
        </ManageDipendentiRoute>
      } />
      <Route path="/documenti" element={
        <DocumentiRoute>
          <Layout><Documenti /></Layout>
        </DocumentiRoute>
      } />
      <Route path="/profilo" element={
        <PrivateRoute>
          <Layout><Profilo /></Layout>
        </PrivateRoute>
      } />
      {/* Nuovi moduli aziendali */}
      <Route path="/scuola" element={
        <ScuolaRoute>
          <Layout><Scuola /></Layout>
        </ScuolaRoute>
      } />
      <Route path="/ospedale" element={
        <OspedaleRoute>
          <Layout><Ospedale /></Layout>
        </OspedaleRoute>
      } />
      <Route path="/banca" element={
        <BancaRoute>
          <Layout><Banca /></Layout>
        </BancaRoute>
      } />
      <Route path="/multe" element={
        <MulteRoute>
          <Layout><Multe /></Layout>
        </MulteRoute>
      } />
      <Route path="/seggi" element={
        <SeggiRoute>
          <Layout><Seggi /></Layout>
        </SeggiRoute>
      } />
      <Route path="/negozio" element={
        <PrivateRoute>
          <Layout><Negozio /></Layout>
        </PrivateRoute>
      } />
      <Route path="/assunzioni" element={
        <PrivateRoute>
          <Layout><Assunzioni /></Layout>
        </PrivateRoute>
      } />
      <Route path="/admin-dashboard" element={
        <AdminRoute>
          <Layout><DashboardAdmin /></Layout>
        </AdminRoute>
      } />
      <Route path="/gestione-ban" element={
        <PrivateRoute>
          <Layout><GestioneBan /></Layout>
        </PrivateRoute>
      } />
      <Route path="/forum" element={
        <ForumRoute>
          <Layout><Forum /></Layout>
        </ForumRoute>
      } />
      <Route path="/gestione-aziende" element={
        <PrivateRoute>
          <Layout><GestioneAziende /></Layout>
        </PrivateRoute>
      } />
      <Route path="/azienda/:aziendaId" element={
        <PrivateRoute>
          <Layout><GestionaleAzienda /></Layout>
        </PrivateRoute>
      } />
      <Route path="/patenti" element={
        <PrivateRoute>
          <Layout><Patenti /></Layout>
        </PrivateRoute>
      } />
      <Route path="/partiti" element={
        <PrivateRoute>
          <Layout><Partiti /></Layout>
        </PrivateRoute>
      } />
      <Route path="/admin-log" element={
        <AdminRoute>
          <Layout><AdminLog /></Layout>
        </AdminRoute>
      } />
      <Route path="/partite-iva" element={
        <PrivateRoute>
          <Layout><PartiteIVA /></Layout>
        </PrivateRoute>
      } />
      <Route path="/store" element={
        <PrivateRoute>
          <Layout><Negozio /></Layout>
        </PrivateRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
